import { useCallback, useMemo } from 'react';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { FileUploadApiCallsContext } from '@trinity/chat-components/containers/WorkspaceFileUpload/Context.js';
import { type Effects } from '@trinity/chat-components/containers/WorkspaceFileUpload/interfaces.effects.js';
import { useCompleteFileUpload } from './useCompleteFileUpload';
import { useInvalidateFilesQuery } from './useInvalidateFilesQuery';

type Props = {
  children: React.ReactNode;
};

export const FileEffectsProvider = (props: Props) => {

  const onComplete = useCompleteFileUpload();
  const invalidateQuery = useInvalidateFilesQuery();
  const complete: Effects.OnCompleteUpload.Fn = useCallback(data => {
    return onComplete(data).then(res => {
      invalidateQuery();
      return res;
    });
  }, [
    invalidateQuery,
    onComplete,
  ]);

  const apiClient = useChatApiClient();

  const abort: Effects.OnAbortUpload.Fn = useCallback(data => {
    return apiClient.workspacesFileUpload.abortFileUpload(data);
  }, [
    apiClient,
  ]);

  const start: Effects.OnStartUpload.Fn = useCallback(data => {
    return apiClient.workspacesFileUpload.startFileUpload(data);
  }, [
    apiClient,
  ]);

  const failure: Effects.OnFailUpload.Fn = useCallback(data => {
    return apiClient.workspacesFileUpload.failFileUpload(data);
  }, [
    apiClient,
  ]);

  const queue: Effects.OnQueueUpload.Fn = useCallback(data => {
    return apiClient.workspacesFileUpload.queueFileUpload({
      file: data.file,
      options: data.options,
      parentObjectId: data.parentObjectId,
      uploadIdentifier: data.uploadIdentifier,
      workspaceId: data.workspaceId,
    });
  }, [
    apiClient,
  ]);

  const value: Effects.FileUploadEffects = useMemo(() => ({
    abort,
    complete,
    failure,
    queue,
    start,
  }), [
    abort,
    complete,
    failure,
    queue,
    start,
  ]);

  return (
    <FileUploadApiCallsContext.Provider value={value}>
      {props.children}
    </FileUploadApiCallsContext.Provider>
  );
};